import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { PaginatedResponse } from "@/api/cle-api";
import {
  getInsurancePolicies,
  getInsurancePolicyById,
  getInsurancePolicyPdfBlob,
  openInsurancePolicyDownload,
  reclassifyInsurancePolicy,
  updateInsurancePolicy,
  type InsurancePolicyModel,
  type ReclassifyInsurancePolicyParams,
  type UpdateInsurancePolicyParams,
} from "@/api/cle-api/insurancePolicies";

import {
  mergeEvidenceDetailUpdate,
  reviewThreadAuditTrailQueryKey,
} from "./useReviewThread";

export interface UseInsurancePoliciesParams {
  userId?: string;
  page?: number;
  pageSize?: number;
}

export interface UseInsurancePolicyParams {
  userId?: string;
  insurancePolicyId?: string;
}

export interface DownloadInsurancePolicyParams {
  userId: string;
  insurancePolicyId: string;
}

export const insurancePoliciesQueryKey = ({
  userId,
  page = 1,
  pageSize = 20,
}: UseInsurancePoliciesParams) =>
  ["cle-api", "users", userId, "insurance-policies", page, pageSize] as const;

export const insurancePolicyQueryKey = ({
  userId,
  insurancePolicyId,
}: UseInsurancePolicyParams) =>
  [
    "cle-api",
    "users",
    userId,
    "insurance-policies",
    insurancePolicyId,
  ] as const;

export const insurancePolicyPdfQueryKey = ({
  userId,
  insurancePolicyId,
}: UseInsurancePolicyParams) =>
  [
    "cle-api",
    "users",
    userId,
    "pdf",
    "insurance-policies",
    insurancePolicyId,
  ] as const;

export const useInsurancePolicies = ({
  userId,
  page = 1,
  pageSize = 20,
}: UseInsurancePoliciesParams): UseQueryResult<
  PaginatedResponse<InsurancePolicyModel>,
  Error
> =>
  useQuery({
    queryKey: insurancePoliciesQueryKey({ userId, page, pageSize }),
    queryFn: () =>
      getInsurancePolicies({
        userId: userId ?? "",
        page,
        pageSize,
      }),
    enabled: !!userId,
  });

export const useInsurancePolicy = ({
  userId,
  insurancePolicyId,
}: UseInsurancePolicyParams): UseQueryResult<InsurancePolicyModel, Error> =>
  useQuery({
    queryKey: insurancePolicyQueryKey({ userId, insurancePolicyId }),
    queryFn: () =>
      getInsurancePolicyById({
        userId: userId ?? "",
        insurancePolicyId: insurancePolicyId ?? "",
      }),
    enabled: !!userId && !!insurancePolicyId,
    retry: false,
  });

export const useDownloadInsurancePolicy = (): UseMutationResult<
  void,
  Error,
  DownloadInsurancePolicyParams
> =>
  useMutation({
    mutationFn: openInsurancePolicyDownload,
  });

export const useInsurancePolicyPdf = ({
  userId,
  insurancePolicyId,
}: UseInsurancePolicyParams): UseQueryResult<Blob, Error> =>
  useQuery({
    queryKey: insurancePolicyPdfQueryKey({ userId, insurancePolicyId }),
    queryFn: () =>
      getInsurancePolicyPdfBlob({
        userId: userId ?? "",
        insurancePolicyId: insurancePolicyId ?? "",
      }),
    enabled: !!userId && !!insurancePolicyId,
  });

export const useUpdateInsurancePolicy = (): UseMutationResult<
  InsurancePolicyModel,
  Error,
  UpdateInsurancePolicyParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateInsurancePolicy,
    onSuccess: (data, { id, userId }) => {
      queryClient.setQueryData<InsurancePolicyModel>(
        insurancePolicyQueryKey({ userId, insurancePolicyId: id }),
        (old) => mergeEvidenceDetailUpdate(old, data),
      );
      void queryClient.invalidateQueries({
        queryKey: reviewThreadAuditTrailQueryKey(id),
      });
      void queryClient.invalidateQueries({
        queryKey: ["cle-api", "users", userId, "insurance-policies"],
      });
    },
  });
};

export const useReclassifyInsurancePolicy = (): UseMutationResult<
  InsurancePolicyModel,
  Error,
  ReclassifyInsurancePolicyParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, id, newStatus }) => {
      await reclassifyInsurancePolicy({ userId, id, newStatus });
      return getInsurancePolicyById({
        userId,
        insurancePolicyId: id,
      });
    },
    onSuccess: (data, { id, userId }) => {
      queryClient.setQueryData<InsurancePolicyModel>(
        insurancePolicyQueryKey({ userId, insurancePolicyId: id }),
        (old) => mergeEvidenceDetailUpdate(old, data),
      );
      void queryClient.invalidateQueries({
        queryKey: reviewThreadAuditTrailQueryKey(id),
      });
      void queryClient.invalidateQueries({
        queryKey: ["cle-api", "admin", "review-threads"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["cle-api", "users", userId, "insurance-policies"],
      });
    },
  });
};
